import React, { useState, useEffect, useCallback } from 'react';
import {
    Table, Button, Modal, Form, InputNumber, Select,
    Space, Tag, Typography, message, Popconfirm,
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined,
} from '@ant-design/icons';
import AdminLayout from '../Layout/AdminLayout';
import axiosInstance from '../../utils/axiosConfig';

const { Text } = Typography;
const { Option } = Select;

const CURRENT_YEAR = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 10 }, (_, i) => CURRENT_YEAR - i);

const AdminBenchmarkManager = () => {
    const [benchmarks, setBenchmarks]   = useState([]);
    const [subjects, setSubjects]       = useState([]);
    const [loading, setLoading]         = useState(false);
    const [filterYear, setFilterYear]   = useState(null);

    const [modalOpen, setModalOpen]     = useState(false);
    const [editRecord, setEditRecord]   = useState(null);
    const [saving, setSaving]           = useState(false);

    const [form] = Form.useForm();

    // ── Fetch dữ liệu ────────────────────────────────────────────────────────
    const fetchData = useCallback(async (year = null) => {
        setLoading(true);
        try {
            const params = {};
            if (year) params.year = year;

            const [benchRes, subjectRes] = await Promise.all([
                axiosInstance.get('/api/v1/admin/admission-benchmarks', { params }),
                axiosInstance.get('/api/v1/admin/subjects-with-combinations'),
            ]);

            setBenchmarks(benchRes.data?.data ?? []);
            setSubjects(subjectRes.data?.data ?? []);
        } catch {
            message.error('Không thể tải dữ liệu. Vui lòng thử lại.');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchData(filterYear); }, [fetchData, filterYear]);

    // ── Mở modal thêm / sửa ──────────────────────────────────────────────────
    const openAdd = () => {
        setEditRecord(null);
        form.resetFields();
        form.setFieldsValue({ year: CURRENT_YEAR });
        setModalOpen(true);
    };

    const openEdit = (record) => {
        setEditRecord(record);
        form.setFieldsValue({
            subject_id:   record.subject_id,
            year:         record.year,
            cutoff_score: record.cutoff_score,
        });
        setModalOpen(true);
    };

    const handleSave = async () => {
        try {
            const values = await form.validateFields();
            setSaving(true);

            if (editRecord) {
                await axiosInstance.put(`/api/v1/admin/admission-benchmarks/${editRecord.id}`, values);
                message.success('Đã cập nhật điểm chuẩn.');
            } else {
                await axiosInstance.post('/api/v1/admin/admission-benchmarks', values);
                message.success('Đã thêm điểm chuẩn.');
            }

            setModalOpen(false);
            fetchData(filterYear);
        } catch (err) {
            if (err?.errorFields) return; // validation
            const msg = err.response?.data?.errors
                ? Object.values(err.response.data.errors).flat().join(', ')
                : 'Lưu thất bại. Vui lòng thử lại.';
            message.error(msg);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id) => {
        try {
            await axiosInstance.delete(`/api/v1/admin/admission-benchmarks/${id}`);
            message.success('Đã xóa.');
            fetchData(filterYear);
        } catch {
            message.error('Xóa thất bại.');
        }
    };

    // ── Cột bảng ─────────────────────────────────────────────────────────────
    const columns = [
        {
            title: 'Năm',
            dataIndex: 'year',
            width: 80,
            sorter: (a, b) => b.year - a.year,
            defaultSortOrder: 'ascend',
            render: (v) => <Tag color="blue">{v}</Tag>,
        },
        {
            title: 'Mã ngành',
            width: 100,
            render: (_, r) => <Text code>{r.subject?.code}</Text>,
        },
        {
            title: 'Tên ngành',
            render: (_, r) => r.subject?.name,
            ellipsis: true,
        },
        {
            title: 'Điểm chuẩn',
            dataIndex: 'cutoff_score',
            width: 120,
            align: 'center',
            sorter: (a, b) => a.cutoff_score - b.cutoff_score,
            render: (v) => (
                <Text strong style={{ color: '#1890ff', fontSize: 15 }}>{v?.toFixed(2)}</Text>
            ),
        },
        {
            title: '',
            width: 90,
            align: 'center',
            render: (_, record) => (
                <Space>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => openEdit(record)}
                    />
                    <Popconfirm
                        title="Xác nhận xóa điểm chuẩn này?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Xóa" cancelText="Hủy"
                    >
                        <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <AdminLayout title="Điểm chuẩn trúng tuyển">
            {/* Toolbar */}
            <Space style={{ marginBottom: 16 }} wrap>
                <Select
                    placeholder="Lọc theo năm"
                    allowClear
                    style={{ width: 140 }}
                    value={filterYear}
                    onChange={(v) => setFilterYear(v ?? null)}
                >
                    {YEAR_OPTIONS.map(y => (
                        <Option key={y} value={y}>{y}</Option>
                    ))}
                </Select>
                <Button type="primary" icon={<PlusOutlined />} onClick={openAdd}>
                    Thêm điểm chuẩn
                </Button>
                {benchmarks.length > 0 && (
                    <Text type="secondary">
                        {benchmarks.length} bản ghi{filterYear ? ` (năm ${filterYear})` : ''}
                    </Text>
                )}
            </Space>

            <Table
                columns={columns}
                dataSource={benchmarks}
                rowKey="id"
                loading={loading}
                size="small"
                pagination={{ pageSize: 25, showTotal: (t) => `Tổng ${t} bản ghi` }}
            />

            {/* Modal thêm / sửa */}
            <Modal
                title={editRecord ? 'Sửa điểm chuẩn' : 'Thêm điểm chuẩn'}
                open={modalOpen}
                onOk={handleSave}
                onCancel={() => setModalOpen(false)}
                confirmLoading={saving}
                okText={editRecord ? 'Cập nhật' : 'Thêm'}
                cancelText="Hủy"
                destroyOnClose
            >
                <Form form={form} layout="vertical" style={{ marginTop: 16 }}>
                    <Form.Item
                        name="subject_id"
                        label="Ngành xét tuyển"
                        rules={[{ required: true, message: 'Vui lòng chọn ngành.' }]}
                    >
                        <Select
                            showSearch
                            placeholder="Chọn ngành..."
                            optionFilterProp="children"
                            filterOption={(input, option) =>
                                (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                            }
                            disabled={!!editRecord}
                            options={subjects.map(s => ({
                                value: s.id,
                                label: `${s.code} — ${s.name}`,
                            }))}
                        />
                    </Form.Item>

                    <Form.Item
                        name="year"
                        label="Năm tuyển sinh"
                        rules={[{ required: true, message: 'Vui lòng chọn năm.' }]}
                    >
                        <Select disabled={!!editRecord}>
                            {YEAR_OPTIONS.map(y => (
                                <Option key={y} value={y}>{y}</Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="cutoff_score"
                        label="Điểm chuẩn (0 – 30)"
                        rules={[{ required: true, message: 'Vui lòng nhập điểm chuẩn.' }]}
                    >
                        <InputNumber
                            min={0} max={30} step={0.05}
                            precision={2}
                            style={{ width: '100%' }}
                            placeholder="VD: 27.50"
                        />
                    </Form.Item>

                </Form>
            </Modal>
        </AdminLayout>
    );
};

export default AdminBenchmarkManager;